import { UserRoleEnum } from '~enums';

import { SideMenuEntry, SideMenuItemType } from './types';

/** Predicate deciding whether an entry's roles grant the current user access. */
export type RoleCheck = (roles?: UserRoleEnum[]) => boolean;

/**
 * Returns a copy of `entry` with anything the user can't see removed, or `null`
 * when the whole entry should be dropped (incl. groups left empty after
 * pruning their children).
 */
const pruneEntry = (entry: SideMenuEntry, isVisible: RoleCheck): SideMenuEntry | null => {
	if (!isVisible(entry.roles)) {
		return null;
	}
	if (entry.type === SideMenuItemType.Section) {
		const items = entry.items
			.filter((item) => isVisible(item.roles))
			.map((item) => {
				if (item.type !== SideMenuItemType.Dropdown) {
					return item;
				}
				const pages = item.pages.filter((page) => isVisible(page.roles));
				return pages.length > 0 ? { ...item, pages } : null;
			})
			.filter((item): item is NonNullable<typeof item> => item !== null);
		return items.length > 0 ? { ...entry, items } : null;
	}
	if (entry.type === SideMenuItemType.Dropdown) {
		const pages = entry.pages.filter((page) => isVisible(page.roles));
		return pages.length > 0 ? { ...entry, pages } : null;
	}
	return entry;
};

/** Prunes a list of entries by role, dropping anything left empty. */
export const pruneEntries = (entries: SideMenuEntry[], isVisible: RoleCheck): SideMenuEntry[] =>
	entries
		.map((entry) => pruneEntry(entry, isVisible))
		.filter((e): e is SideMenuEntry => e !== null);

/** A stable React key for an entry (labels/titles are unique within the menu). */
export const entryKey = (entry: SideMenuEntry) =>
	entry.type === SideMenuItemType.Page ? entry.label : entry.title;
